import clsx from 'clsx';
import {
  Activity,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Database,
  Download,
  Medal,
  RefreshCw,
  Search,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchJSON } from '../api';
import { apiPath, FLOW } from '../constants';
import { useCsvExport } from '../csvExport';
import { useStore } from '../store';
import { showToast } from '../toast';
import type {
  DailyAuditWorkloadRow,
  DailyAuditWorkloadSummary,
  DailyWorkloadResponse,
  DailyWorkloadRow,
  DailyWorkloadSummary,
  EpisodeStatsOperator,
  ModelVoteDurationResponse,
  WorkloadPagination,
} from '../types';
import { CsvExportProgress } from './CsvExportProgress';
import {
  addDays,
  DateRangePicker,
  type DateTimeRangeValue,
  dateTimeFilenamePart,
  localDateTimeMs,
  toDateInputValue,
} from './DateRangePicker';
import { EdgeOnePrefetchAdmin } from './EdgeOnePrefetchAdmin';

type LeaderboardSortMode = 'frames' | 'episodes' | 'audited';
type WorkloadDisplayUnit = 'episodes' | 'frames';
type ZeroScoreRole = 'all' | 'annotate' | 'audit';
type ZeroScoreView = 'summary' | 'detail';

type ProductionVoteDiagnostics = {
  computed_at_ms: number;
  required_production_votes: number;
  expected_human_votes: number;
  model_operator: string | null;
  pending: Record<string, number>;
  voting: Record<string, number> & {
    with_model_vote?: number;
    full_production_votes?: number;
    waiting_human_confirmation?: number;
    ready_to_finalize?: number;
    excess_production_votes?: number;
  };
  completed: Record<string, number> & {
    with_model_vote?: number;
    eligible_for_audit?: number;
    incomplete_production_votes?: number;
    legacy_operator_count_mismatch?: number;
    excess_production_votes?: number;
  };
  samples: Array<{
    episode_id: string;
    operator_count: number;
    human_vote_count: number;
    all_vote_count: number;
    open_amend_votes: number;
    claimable_now: number;
  }>;
};

const ACCURACY_DEFAULT_DAYS = 7;
const ACCURACY_SLOW_WARN_DAYS = 7;
const DAILY_WORKLOAD_DEFAULT_DAYS = 7;
const DAILY_WORKLOAD_MAX_DAYS = 90;
const HOURLY_WORKLOAD_MAX_DAYS = 31;
const HOUR_MS = 60 * 60 * 1000;
const SHANGHAI_OFFSET_MS = 8 * HOUR_MS;
const MODEL_DURATION_DEFAULT_DAYS = 7;
const HOURLY_WORKLOAD_PAGE_SIZES = [24, 48, 72] as const;
const DAILY_WORKLOAD_PAGE_SIZES = [14, 30, 60] as const;
const AUDIT_WORKLOAD_PAGE_SIZES = [14, 30, 60] as const;

const EMPTY_WORKLOAD_SUMMARY: DailyWorkloadSummary = {
  new_episodes: 0,
  new_frames: 0,
  voting: 0,
  voting_frames: 0,
  voting_video_duration_s: 0,
  to_audit: 0,
  to_audit_frames: 0,
  to_audit_video_duration_s: 0,
  audited: 0,
  audited_frames: 0,
  audited_video_duration_s: 0,
};

const EMPTY_AUDIT_SUMMARY: DailyAuditWorkloadSummary = {
  to_audit_episodes: 0,
  to_audit_full_frames: 0,
  to_audit_frames: 0,
  full_hours: 0,
  actual_audit_hours: 0,
  coverage_pct: 0,
};

function emptyPagination(pageSize: number): WorkloadPagination {
  return { page: 1, page_size: pageSize, total_rows: 0, total_pages: 0 };
}

function PaginationControls({
  pagination,
  pageSizes,
  onPageChange,
  onPageSizeChange,
  testId,
}: {
  pagination: WorkloadPagination;
  pageSizes: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  testId: string;
}) {
  const firstRow =
    pagination.total_rows === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const lastRow = Math.min(pagination.page * pagination.page_size, pagination.total_rows);
  const canGoBack = pagination.page > 1;
  const canGoForward = pagination.page < pagination.total_pages;
  const buttonClass =
    'flex h-8 w-8 items-center justify-center rounded-md border border-brand-border bg-brand-dark text-brand-text-secondary transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-border px-3 py-3 text-xs text-brand-text-secondary"
      data-testid={testId}
    >
      <span>
        {firstRow.toLocaleString()}–{lastRow.toLocaleString()} of{' '}
        {pagination.total_rows.toLocaleString()}
      </span>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2">
          <span>Rows</span>
          <select
            value={pagination.page_size}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="h-8 rounded-md border border-brand-border bg-brand-dark px-2 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
            aria-label="Rows per page"
          >
            {pageSizes.map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </label>
        <span className="min-w-20 text-center">
          Page {pagination.page} of {Math.max(pagination.total_pages, 1)}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={buttonClass}
            onClick={() => onPageChange(1)}
            disabled={!canGoBack}
            title="First page"
            aria-label="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={buttonClass}
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={!canGoBack}
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={buttonClass}
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={!canGoForward}
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={buttonClass}
            onClick={() => onPageChange(pagination.total_pages)}
            disabled={!canGoForward}
            title="Last page"
            aria-label="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Per-flow caps from prod row-count analysis (30s frontend + DO CPU budget).
// Must stay in sync with workers/src/exportLimits.ts.
const FLOW_LEADERBOARD_MAX_DAYS: Record<string, number> = {
  secshot: 45,
  seccap: 90,
  tas_design: 90,
};
const FLOW_ACCURACY_MAX_DAYS: Record<string, number> = {
  secshot: 7,
  seccap: 9,
  tas_design: 30,
};
const DEFAULT_LEADERBOARD_MAX_DAYS = 45;
const DEFAULT_ACCURACY_MAX_DAYS = 7;
const ZERO_SCORE_SUPPORTED = FLOW === 'secshot' || FLOW === 'seccap';
const QUEUE_DIAGNOSTICS_SUPPORTED = FLOW === 'secshot' || FLOW === 'seccap';
const HOURLY_WORKLOAD_SUPPORTED = FLOW === 'seccap';
const videoHoursFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function videoDurationSeconds(seconds: number | undefined): number {
  return seconds != null && Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
}

function formatVideoHours(seconds: number | undefined): string {
  return videoHoursFormatter.format(videoDurationSeconds(seconds) / 3600);
}

function toShanghaiHourInputValue(ms: number): string {
  return `${new Date(ms + SHANGHAI_OFFSET_MS).toISOString().slice(0, 13)}:00`;
}

function shanghaiHourInputMs(value: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):00$/.exec(value);
  if (!match) return Number.NaN;
  return (
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), 0, 0, 0) -
    SHANGHAI_OFFSET_MS
  );
}

function formatShanghaiHourRange(startMs: number | undefined, endMs: number | undefined): string {
  if (startMs == null || endMs == null) return '—';
  const format = (ms: number): string =>
    new Date(ms + SHANGHAI_OFFSET_MS).toISOString().slice(0, 16).replace('T', ' ');
  return `${format(startMs)}–${format(endMs)}`;
}

function leaderboardMaxDaysForFlow(): number {
  return FLOW_LEADERBOARD_MAX_DAYS[FLOW] ?? DEFAULT_LEADERBOARD_MAX_DAYS;
}

function accuracyMaxDaysForFlow(): number {
  return FLOW_ACCURACY_MAX_DAYS[FLOW] ?? DEFAULT_ACCURACY_MAX_DAYS;
}

// secpose records annotate/audit work in pose_annotations, not votes/audits, so a
// windowed leaderboard export would be silently empty (all-time still works, it
// reads the operator_stats accumulators). Gate the date picker off for secpose —
// its leaderboard buttons export all-time directly. Mirrors the backend guard in
// coordinator.ts (parseLeaderboardRange rejects ?flow=secpose with a range).
const RANGED_LEADERBOARD_EXPORT_SUPPORTED = FLOW !== 'secpose';

type ExportPickerMode = 'accuracy' | 'annotate' | 'audit';

type ExportPickerConfig = {
  title: string;
  hardMaxDays: number | null;
  slowWarnDays: number | null;
  defaultLookbackDays: number;
  path: string;
  filePrefix: string;
  toastOk: string;
  allowAllTime: boolean;
  rangeHint: string;
  confirmKindLabel: string;
};

function exportPickerConfig(mode: ExportPickerMode): ExportPickerConfig {
  const leaderboardMax = leaderboardMaxDaysForFlow();
  const accuracyMax = accuracyMaxDaysForFlow();
  switch (mode) {
    case 'accuracy':
      return {
        title: '导出准确率 + Task Quality CSV',
        hardMaxDays: accuracyMax,
        slowWarnDays: accuracyMax > ACCURACY_SLOW_WARN_DAYS ? ACCURACY_SLOW_WARN_DAYS : null,
        defaultLookbackDays: Math.min(ACCURACY_DEFAULT_DAYS, accuracyMax) - 1,
        path: '/admin/accuracy-csv',
        filePrefix: 'accuracy',
        toastOk: '准确率和 Task Quality 数据已导出',
        allowAllTime: false,
        rangeHint:
          accuracyMax > ACCURACY_SLOW_WARN_DAYS
            ? `超过 ${ACCURACY_SLOW_WARN_DAYS} 天需二次确认（可能较慢）`
            : '默认最近 7 天',
        confirmKindLabel: '准确率',
      };
    case 'annotate':
      return {
        title: '导出标注 Leaderboard CSV',
        hardMaxDays: leaderboardMax,
        slowWarnDays: null,
        defaultLookbackDays: 1,
        path: '/admin/leaderboard-csv',
        filePrefix: 'annotate_leaderboard',
        toastOk: 'Annotator leaderboard exported to CSV',
        allowAllTime: true,
        rangeHint: '默认昨天至今天；更长历史可用「导出全部时间」',
        confirmKindLabel: '标注 Leaderboard',
      };
    case 'audit':
      return {
        title: '导出审计 Leaderboard CSV',
        hardMaxDays: leaderboardMax,
        slowWarnDays: null,
        defaultLookbackDays: 1,
        path: '/admin/audit-leaderboard-csv',
        filePrefix: 'audit_leaderboard',
        toastOk: 'Auditor leaderboard exported to CSV',
        allowAllTime: true,
        rangeHint: '默认昨天至今天；更长历史可用「导出全部时间」',
        confirmKindLabel: '审计 Leaderboard',
      };
  }
}

// Annotator accuracy from the per-operator accumulator (operator_stats.accuracy_*,
// surfaced by /stats). null when the operator has no graded episode yet.
function operatorAccuracy(op: EpisodeStatsOperator): number | null {
  const total = op.accuracy_total_bits ?? 0;
  if (total <= 0) return null;
  return (op.accuracy_match_bits ?? 0) / total;
}

function operatorTaskQualityAccuracy(op: EpisodeStatsOperator): number | null {
  const total = op.task_quality_total_count ?? 0;
  if (total <= 0) return null;
  return (op.task_quality_match_count ?? 0) / total;
}

function formatDurationHours(durationSeconds: number): string {
  return (durationSeconds / 3600).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDurationBreakdown(durationSeconds: number): string {
  const totalSeconds = Math.max(0, Math.round(durationSeconds));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${days}d ${hours}h ${minutes}m`;
}

function sortAndFilterOperators(
  operators: EpisodeStatsOperator[],
  options?: { query?: string; sortMode?: LeaderboardSortMode }
): EpisodeStatsOperator[] {
  const query = (options?.query ?? '').trim().toLowerCase();
  const sortMode = options?.sortMode ?? 'frames';

  const filtered = query
    ? operators.filter((operator) => operator.name.toLowerCase().includes(query))
    : [...operators];

  filtered.sort((a, b) => {
    if (sortMode === 'episodes') {
      if (b.episodes !== a.episodes) return b.episodes - a.episodes;
      if (b.images !== a.images) return b.images - a.images;
      return a.name.localeCompare(b.name);
    }

    if (sortMode === 'audited') {
      const auditedA = a.audited_images || 0;
      const auditedB = b.audited_images || 0;
      if (auditedB !== auditedA) return auditedB - auditedA;
      if (b.images !== a.images) return b.images - a.images;
      return a.name.localeCompare(b.name);
    }

    if (b.images !== a.images) return b.images - a.images;
    if (b.episodes !== a.episodes) return b.episodes - a.episodes;
    return a.name.localeCompare(b.name);
  });

  return filtered;
}

export function AdminStats() {
  const { statsCache, fetchStatsData, refreshAnnotatedImages, showDashboard } = useStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [leaderboardQuery, setLeaderboardQuery] = useState('');
  const [leaderboardSortMode, setLeaderboardSortMode] = useState<LeaderboardSortMode>('frames');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<ExportPickerMode | null>(null);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [rangeError, setRangeError] = useState('');
  const [slowExportConfirm, setSlowExportConfirm] = useState<{
    mode: ExportPickerMode;
    diffDays: number;
  } | null>(null);
  const [zeroScoreOpen, setZeroScoreOpen] = useState(false);
  const [zeroScoreRole, setZeroScoreRole] = useState<ZeroScoreRole>('all');
  const [zeroScoreView, setZeroScoreView] = useState<ZeroScoreView>('summary');
  const [zeroScoreRange, setZeroScoreRange] = useState<DateTimeRangeValue>(() => {
    const today = new Date();
    return {
      fromDate: toDateInputValue(addDays(today, -6)),
      fromTime: '00:00',
      toDate: toDateInputValue(today),
      toTime: '23:59',
    };
  });
  const [zeroScoreError, setZeroScoreError] = useState('');
  const [dailyFrom, setDailyFrom] = useState(() => {
    if (HOURLY_WORKLOAD_SUPPORTED) {
      const completeHourEnd = Math.floor(Date.now() / HOUR_MS) * HOUR_MS;
      return toShanghaiHourInputValue(completeHourEnd - 24 * HOUR_MS);
    }
    return toDateInputValue(addDays(new Date(), -(DAILY_WORKLOAD_DEFAULT_DAYS - 1)));
  });
  const [dailyTo, setDailyTo] = useState(() => {
    if (HOURLY_WORKLOAD_SUPPORTED) {
      return toShanghaiHourInputValue(Math.floor(Date.now() / HOUR_MS) * HOUR_MS);
    }
    return toDateInputValue(new Date());
  });
  const [dailyRows, setDailyRows] = useState<DailyWorkloadRow[]>([]);
  const defaultWorkloadPageSize = HOURLY_WORKLOAD_SUPPORTED ? 24 : 14;
  const [workloadPage, setWorkloadPage] = useState(1);
  const [workloadPageSize, setWorkloadPageSize] = useState(defaultWorkloadPageSize);
  const [workloadPagination, setWorkloadPagination] = useState<WorkloadPagination>(() =>
    emptyPagination(defaultWorkloadPageSize)
  );
  const [workloadSummary, setWorkloadSummary] =
    useState<DailyWorkloadSummary>(EMPTY_WORKLOAD_SUMMARY);
  const [workloadDisplayUnit, setWorkloadDisplayUnit] = useState<WorkloadDisplayUnit>('episodes');
  const [dailyAuditRows, setDailyAuditRows] = useState<DailyAuditWorkloadRow[]>([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(14);
  const [auditPagination, setAuditPagination] = useState<WorkloadPagination>(() =>
    emptyPagination(14)
  );
  const [auditSummary, setAuditSummary] = useState<DailyAuditWorkloadSummary>(EMPTY_AUDIT_SUMMARY);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState('');
  const [modelDurationRange, setModelDurationRange] = useState<DateTimeRangeValue>(() => {
    const today = new Date();
    return {
      fromDate: toDateInputValue(addDays(today, -(MODEL_DURATION_DEFAULT_DAYS - 1))),
      fromTime: '00:00',
      toDate: toDateInputValue(today),
      toTime: '23:59',
    };
  });
  const [modelDuration, setModelDuration] = useState<ModelVoteDurationResponse | null>(null);
  const [modelDurationLoading, setModelDurationLoading] = useState(false);
  const [modelDurationError, setModelDurationError] = useState('');
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsError, setDiagnosticsError] = useState('');
  const [diagnostics, setDiagnostics] = useState<ProductionVoteDiagnostics | null>(null);
  const {
    exportState,
    isExporting: isCsvExporting,
    downloadCsv,
    clearExportState,
  } = useCsvExport();

  const dailyRangeMs = useCallback((): {
    fromMs: number;
    toMs: number;
    diffDays: number;
  } | null => {
    const fromMs = HOURLY_WORKLOAD_SUPPORTED
      ? shanghaiHourInputMs(dailyFrom)
      : localDateTimeMs(dailyFrom, '00:00', false);
    const toMs = HOURLY_WORKLOAD_SUPPORTED
      ? shanghaiHourInputMs(dailyTo)
      : localDateTimeMs(dailyTo, '23:59', true);
    if (!dailyFrom || !dailyTo || Number.isNaN(fromMs) || Number.isNaN(toMs)) {
      setDailyError(
        HOURLY_WORKLOAD_SUPPORTED
          ? '请选择精确到整点的开始和结束时间。'
          : '请选择完整的开始和结束日期。'
      );
      return null;
    }
    if (toMs <= fromMs) {
      setDailyError(
        HOURLY_WORKLOAD_SUPPORTED ? '结束时间必须晚于开始时间。' : '结束日期必须晚于开始日期。'
      );
      return null;
    }
    const diffDays = Math.ceil((toMs - fromMs) / (24 * 60 * 60 * 1000));
    const maxDays = HOURLY_WORKLOAD_SUPPORTED ? HOURLY_WORKLOAD_MAX_DAYS : DAILY_WORKLOAD_MAX_DAYS;
    if (diffDays > maxDays) {
      setDailyError(`${HOURLY_WORKLOAD_SUPPORTED ? '小时' : '日期'}范围不能超过 ${maxDays} 天。`);
      return null;
    }
    return { fromMs, toMs, diffDays };
  }, [dailyFrom, dailyTo]);

  const loadDailyWorkload = useCallback(async () => {
    const range = dailyRangeMs();
    if (!range) return;
    setDailyLoading(true);
    setDailyError('');
    try {
      const params = new URLSearchParams({
        from: String(range.fromMs),
        to: String(range.toMs),
        workload_page: String(workloadPage),
        workload_page_size: String(workloadPageSize),
        audit_page: String(auditPage),
        audit_page_size: String(auditPageSize),
      });
      if (HOURLY_WORKLOAD_SUPPORTED) params.set('bucket', 'hour');
      const data = await fetchJSON<DailyWorkloadResponse>(
        apiPath(`/admin/daily-workload?${params}`)
      );
      setDailyRows(data?.rows ?? []);
      setDailyAuditRows(data?.audit_rows ?? []);
      const nextWorkloadPagination = data?.workload_pagination ?? {
        page: workloadPage,
        page_size: workloadPageSize,
        total_rows: data?.rows?.length ?? 0,
        total_pages: (data?.rows?.length ?? 0) > 0 ? 1 : 0,
      };
      const nextAuditPagination = data?.audit_pagination ?? {
        page: auditPage,
        page_size: auditPageSize,
        total_rows: data?.audit_rows?.length ?? 0,
        total_pages: (data?.audit_rows?.length ?? 0) > 0 ? 1 : 0,
      };
      setWorkloadPagination(nextWorkloadPagination);
      setAuditPagination(nextAuditPagination);
      setWorkloadPage(nextWorkloadPagination.page);
      setAuditPage(nextAuditPagination.page);
      setWorkloadSummary(
        data?.workload_summary ??
          (data?.rows ?? []).reduce<DailyWorkloadSummary>(
            (summary, row) => ({
              new_episodes: summary.new_episodes + row.new_episodes,
              new_frames: summary.new_frames + row.new_frames,
              voting: summary.voting + row.voting,
              voting_frames: summary.voting_frames + (row.voting_frames ?? 0),
              voting_video_duration_s:
                summary.voting_video_duration_s + videoDurationSeconds(row.voting_video_duration_s),
              to_audit: summary.to_audit + row.to_audit,
              to_audit_frames: summary.to_audit_frames + (row.to_audit_frames ?? 0),
              to_audit_video_duration_s:
                summary.to_audit_video_duration_s +
                videoDurationSeconds(row.to_audit_video_duration_s),
              audited: summary.audited + row.audited,
              audited_frames: summary.audited_frames + (row.audited_frames ?? 0),
              audited_video_duration_s:
                summary.audited_video_duration_s +
                videoDurationSeconds(row.audited_video_duration_s),
            }),
            { ...EMPTY_WORKLOAD_SUMMARY }
          )
      );
      setAuditSummary(
        data?.audit_summary ??
          (data?.audit_rows ?? []).reduce<DailyAuditWorkloadSummary>(
            (summary, row) => {
              const fullFrames = summary.to_audit_full_frames + row.to_audit_full_frames;
              const auditFrames = summary.to_audit_frames + row.to_audit_frames;
              return {
                to_audit_episodes: summary.to_audit_episodes + row.to_audit_episodes,
                to_audit_full_frames: fullFrames,
                to_audit_frames: auditFrames,
                full_hours: summary.full_hours + row.full_hours,
                actual_audit_hours: summary.actual_audit_hours + row.actual_audit_hours,
                coverage_pct: fullFrames > 0 ? (auditFrames / fullFrames) * 100 : 0,
              };
            },
            { ...EMPTY_AUDIT_SUMMARY }
          )
      );
    } catch (err) {
      console.error('Failed to load daily workload', err);
      setDailyError('每日新增作业量加载失败');
      showToast('每日新增作业量加载失败');
    } finally {
      setDailyLoading(false);
    }
  }, [auditPage, auditPageSize, dailyRangeMs, workloadPage, workloadPageSize]);

  const loadModelDuration = useCallback(async (range: DateTimeRangeValue) => {
    if (FLOW !== 'seccap') return;
    const fromMs = localDateTimeMs(range.fromDate, range.fromTime, false);
    const toMs = localDateTimeMs(range.toDate, range.toTime, true);
    if (!range.fromDate || !range.toDate || Number.isNaN(fromMs) || Number.isNaN(toMs)) {
      setModelDurationError('请选择完整的开始和结束日期。');
      return;
    }
    if (toMs <= fromMs) {
      setModelDurationError('结束时间必须晚于开始时间。');
      return;
    }
    const diffDays = Math.ceil((toMs - fromMs) / (24 * 60 * 60 * 1000));
    const maxDays = leaderboardMaxDaysForFlow();
    if (diffDays > maxDays) {
      setModelDurationError(`日期范围不能超过 ${maxDays} 天。`);
      return;
    }

    setModelDurationLoading(true);
    setModelDurationError('');
    try {
      const params = new URLSearchParams({ from: String(fromMs), to: String(toMs) });
      const data = await fetchJSON<ModelVoteDurationResponse>(
        apiPath(`/admin/model-vote-duration?${params}`)
      );
      setModelDuration(data);
    } catch (err) {
      console.error('Failed to load model vote duration', err);
      setModelDurationError('ALM Bot 总时长加载失败，请重试。');
    } finally {
      setModelDurationLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      await fetchStatsData();
      refreshAnnotatedImages();
      setLastUpdated(new Date());
    };
    void load();
  }, [fetchStatsData, refreshAnnotatedImages]);

  useEffect(() => {
    void loadDailyWorkload();
  }, [loadDailyWorkload]);

  useEffect(() => {
    void loadModelDuration(modelDurationRange);
  }, [loadModelDuration, modelDurationRange]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchStatsData(true);
    refreshAnnotatedImages();
    await Promise.all([loadDailyWorkload(), loadModelDuration(modelDurationRange)]);
    setLastUpdated(new Date());
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const loadProductionVoteDiagnostics = async () => {
    setDiagnosticsOpen(true);
    setDiagnosticsLoading(true);
    setDiagnosticsError('');
    try {
      const data = await fetchJSON<ProductionVoteDiagnostics>(
        apiPath('/admin/production-vote-diagnostics?limit=20')
      );
      setDiagnostics(data);
    } catch (err) {
      console.error('Failed to load queue diagnostics', err);
      setDiagnosticsError('Queue diagnostics 加载失败，请刷新登录状态后重试。');
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const operators = useMemo(() => statsCache?.operators || [], [statsCache?.operators]);
  const hasAnyData = operators.length > 0;
  const sortedOperators = useMemo(
    () =>
      sortAndFilterOperators(operators, {
        query: leaderboardQuery,
        sortMode: leaderboardSortMode,
      }),
    [operators, leaderboardQuery, leaderboardSortMode]
  );
  const hasData = sortedOperators.length > 0;
  const topAuditor = useMemo(
    () =>
      [...operators].sort((a, b) => {
        const auditedA = a.audited_images || 0;
        const auditedB = b.audited_images || 0;
        if (auditedB !== auditedA) return auditedB - auditedA;
        return b.images - a.images || a.name.localeCompare(b.name);
      })[0] ?? null,
    [operators]
  );
  const totalEpisodes = statsCache?.total_annotated_episodes ?? 0;
  const totalFrames = statsCache?.total_annotated_images ?? 0;
  const totalPending = statsCache?.total_pending_episodes ?? 0;
  const totalAuditedFrames = statsCache?.total_audited_images ?? 0;
  const auditCoverage = totalFrames > 0 ? Math.round((totalAuditedFrames / totalFrames) * 100) : 0;
  const avgFrames = totalEpisodes > 0 ? Math.round(totalFrames / totalEpisodes) : 0;
  const maxFrames = Math.max(...operators.map((o) => o.images), 1);
  const latestScheduledIngestion = statsCache?.latest_scheduled_ingestion ?? null;

  const openExportPicker = (mode: ExportPickerMode) => {
    if (isCsvExporting) {
      showToast('正在导出，请稍候');
      return;
    }
    setExportMenuOpen(false);
    // secpose has no ranged export (see RANGED_LEADERBOARD_EXPORT_SUPPORTED): the
    // leaderboard buttons fall back to the all-time export instead of a picker.
    if (mode !== 'accuracy' && !RANGED_LEADERBOARD_EXPORT_SUPPORTED) {
      void downloadAllTime(mode);
      return;
    }
    const cfg = exportPickerConfig(mode);
    const today = new Date();
    const toDate = today.toISOString().slice(0, 10);
    const fromDate = new Date(today.getTime() - cfg.defaultLookbackDays * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setRangeFrom(fromDate);
    setRangeTo(toDate);
    setRangeError('');
    setSlowExportConfirm(null);
    setPickerMode(mode);
  };

  const openZeroScoreExport = () => {
    if (isCsvExporting) {
      showToast('正在导出，请稍候');
      return;
    }
    setExportMenuOpen(false);
    setZeroScoreError('');
    setZeroScoreOpen(true);
  };

  const parseSelectedRange = (): { fromDate: Date; toDate: Date; diffDays: number } | null => {
    const fromDate = new Date(rangeFrom);
    const toDate = new Date(rangeTo);
    toDate.setDate(toDate.getDate() + 1);
    if (fromDate >= toDate) {
      setRangeError('结束日期必须晚于开始日期');
      return null;
    }
    const diffDays = Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
    return { fromDate, toDate, diffDays };
  };

  const executeDownloadRange = async (mode: ExportPickerMode) => {
    const cfg = exportPickerConfig(mode);
    const parsed = parseSelectedRange();
    if (!parsed) return;
    const { fromDate, toDate, diffDays } = parsed;
    if (cfg.hardMaxDays != null && diffDays > cfg.hardMaxDays) {
      setRangeError(`日期范围不能超过 ${cfg.hardMaxDays} 天（当前选择了 ${diffDays} 天）`);
      return;
    }
    const [from, to] = [rangeFrom, rangeTo];
    setPickerMode(null);
    setSlowExportConfirm(null);
    const label = exportPickerConfig(mode).confirmKindLabel;
    try {
      const params = new URLSearchParams({
        from: String(fromDate.getTime()),
        to: String(toDate.getTime()),
      });
      await downloadCsv({
        path: `${cfg.path}?${params}`,
        filename: `${cfg.filePrefix}_${from}_${to}.csv`,
        label,
      });
      showToast(cfg.toastOk);
    } catch (err) {
      showToast('导出失败');
      console.error('CSV export failed', err);
    } finally {
      setTimeout(clearExportState, 1800);
    }
  };

  const handleDownloadRange = () => {
    if (!pickerMode) return;
    const cfg = exportPickerConfig(pickerMode);
    const parsed = parseSelectedRange();
    if (!parsed) return;
    const { diffDays } = parsed;
    if (cfg.hardMaxDays != null && diffDays > cfg.hardMaxDays) {
      setRangeError(`日期范围不能超过 ${cfg.hardMaxDays} 天（当前选择了 ${diffDays} 天）`);
      return;
    }
    if (cfg.slowWarnDays != null && diffDays > cfg.slowWarnDays) {
      setPickerMode(null);
      setSlowExportConfirm({ mode: pickerMode, diffDays });
      return;
    }
    void executeDownloadRange(pickerMode);
  };

  const downloadAllTime = async (mode: ExportPickerMode) => {
    const cfg = exportPickerConfig(mode);
    const label = `${cfg.confirmKindLabel}全部时间`;
    try {
      await downloadCsv({
        path: cfg.path,
        filename: `${cfg.filePrefix}_${new Date().toISOString().slice(0, 10)}.csv`,
        label,
      });
      showToast(cfg.toastOk);
    } catch (err) {
      showToast('导出失败');
      console.error('CSV export failed', err);
    } finally {
      setTimeout(clearExportState, 1800);
    }
  };

  const handleDownloadAllTime = async () => {
    if (!pickerMode) return;
    const mode = pickerMode;
    setPickerMode(null);
    await downloadAllTime(mode);
  };

  const executeZeroScoreExport = async () => {
    const fromMs = localDateTimeMs(zeroScoreRange.fromDate, zeroScoreRange.fromTime, false);
    const toMs = localDateTimeMs(zeroScoreRange.toDate, zeroScoreRange.toTime, true);
    if (
      !zeroScoreRange.fromDate ||
      !zeroScoreRange.toDate ||
      Number.isNaN(fromMs) ||
      Number.isNaN(toMs)
    ) {
      setZeroScoreError('请选择完整的开始和结束日期。');
      return;
    }
    if (toMs <= fromMs) {
      setZeroScoreError('结束时间必须晚于开始时间。');
      return;
    }
    const diffDays = Math.ceil((toMs - fromMs) / (24 * 60 * 60 * 1000));
    const maxDays = leaderboardMaxDaysForFlow();
    if (diffDays > maxDays) {
      setZeroScoreError(`日期范围不能超过 ${maxDays} 天（当前选择了 ${diffDays} 天）`);
      return;
    }
    setZeroScoreError('');
    const params = new URLSearchParams({
      from: String(fromMs),
      to: String(toMs),
      role: zeroScoreRole,
      view: zeroScoreView,
    });
    const label = '0分量级';
    const fromPart = dateTimeFilenamePart(zeroScoreRange.fromDate, zeroScoreRange.fromTime);
    const toPart = dateTimeFilenamePart(zeroScoreRange.toDate, zeroScoreRange.toTime);
    try {
      await downloadCsv({
        path: `/admin/zero-score-csv?${params}`,
        filename: `zero_score_${zeroScoreView}_${zeroScoreRole}_${fromPart}_${toPart}.csv`,
        label,
      });
      setZeroScoreOpen(false);
      showToast('0分量级 CSV 已导出');
    } catch (err) {
      showToast('导出失败');
      console.error('CSV export failed', err);
    } finally {
      setTimeout(clearExportState, 1800);
    }
  };

  const exportDailyWorkload = async () => {
    if (isCsvExporting) {
      showToast('正在导出，请稍候');
      return;
    }
    const range = dailyRangeMs();
    if (!range) return;
    const params = new URLSearchParams({
      from: String(range.fromMs),
      to: String(range.toMs),
    });
    if (HOURLY_WORKLOAD_SUPPORTED) params.set('bucket', 'hour');
    try {
      await downloadCsv({
        path: `/admin/daily-workload-csv?${params}`,
        filename: `daily_workload_${dailyFrom.replace(':', '')}_${dailyTo.replace(':', '')}.csv`,
        label: '每日新增作业量',
      });
      showToast('每日新增作业量 CSV 已导出');
    } catch (err) {
      showToast('导出失败');
      console.error('Daily workload CSV export failed', err);
    } finally {
      setTimeout(clearExportState, 1800);
    }
  };

  const exportDailyAuditWorkload = async () => {
    if (isCsvExporting) {
      showToast('正在导出，请稍候');
      return;
    }
    const range = dailyRangeMs();
    if (!range) return;
    const params = new URLSearchParams({
      from: String(range.fromMs),
      to: String(range.toMs),
    });
    try {
      await downloadCsv({
        path: `/admin/daily-audit-workload-csv?${params}`,
        filename: `daily_audit_workload_${dailyFrom}_${dailyTo}.csv`,
        label: '每日待审工作量',
      });
      showToast('每日待审工作量 CSV 已导出');
    } catch (err) {
      showToast('导出失败');
      console.error('Daily audit workload CSV export failed', err);
    } finally {
      setTimeout(clearExportState, 1800);
    }
  };

  const dailyTotals = {
    episodes: workloadSummary.new_episodes,
    frames: workloadSummary.new_frames,
    voting: workloadSummary.voting,
    votingFrames: workloadSummary.voting_frames,
    votingVideoDurationS: workloadSummary.voting_video_duration_s,
    toAudit: workloadSummary.to_audit,
    toAuditFrames: workloadSummary.to_audit_frames,
    toAuditVideoDurationS: workloadSummary.to_audit_video_duration_s,
    audited: workloadSummary.audited,
    auditedFrames: workloadSummary.audited_frames,
    auditedVideoDurationS: workloadSummary.audited_video_duration_s,
  };
  const dailyAuditTotals = {
    episodes: auditSummary.to_audit_episodes,
    fullFrames: auditSummary.to_audit_full_frames,
    auditFrames: auditSummary.to_audit_frames,
    fullHours: auditSummary.full_hours,
    auditHours: auditSummary.actual_audit_hours,
  };
  const dailyAuditCoverage = auditSummary.coverage_pct;

  const applyDailyRange = () => {
    if (workloadPage !== 1 || auditPage !== 1) {
      setWorkloadPage(1);
      setAuditPage(1);
      return;
    }
    void loadDailyWorkload();
  };

  return (
    <div className="mx-auto max-w-5xl space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-brand-border pb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <Trophy className="h-8 w-8 text-brand-orange" />
            Leaderboard
          </h1>
          <p className="mt-2 text-brand-text-secondary">
            Top performing operators by annotation volume.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {lastUpdated && (
            <span className="text-xs text-brand-text-secondary">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={() => openExportPicker('annotate')}
            disabled={!hasData || isCsvExporting}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
            title={`按日期范围导出（最多 ${leaderboardMaxDaysForFlow()} 天）或全部时间`}
          >
            <Download className="h-3.5 w-3.5" />
            Export Annotator Leaderboard
          </button>
          <button
            type="button"
            onClick={() => openExportPicker('audit')}
            disabled={!hasData || isCsvExporting}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
            title={`按日期范围导出（最多 ${leaderboardMaxDaysForFlow()} 天）或全部时间`}
          >
            <Download className="h-3.5 w-3.5" />
            Export Auditor Leaderboard
          </button>
          {ZERO_SCORE_SUPPORTED && (
            <button
              type="button"
              onClick={openZeroScoreExport}
              disabled={isCsvExporting}
              className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
              title="按日期范围导出 0 分标注 / 审计量级"
            >
              <Download className="h-3.5 w-3.5" />
              Export Zero-score Work
            </button>
          )}
          <button
            type="button"
            onClick={() => openExportPicker('accuracy')}
            disabled={!hasData || isCsvExporting}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
            title={`按日期范围导出准确率和 Task Quality CSV（最多 ${accuracyMaxDaysForFlow()} 天）`}
          >
            <Download className="h-3.5 w-3.5" />
            Export Accuracy + TQ
          </button>
          {QUEUE_DIAGNOSTICS_SUPPORTED && (
            <button
              type="button"
              onClick={() => void loadProductionVoteDiagnostics()}
              disabled={diagnosticsLoading}
              className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
              title="查看当前生产领取队列，只读"
            >
              <Activity className={clsx('h-3.5 w-3.5', diagnosticsLoading && 'animate-pulse')} />
              Queue Diagnostics
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleRefresh()}
            disabled={isRefreshing || isCsvExporting}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => void showDashboard()}
            className="self-start sm:self-center flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-4 py-2 text-sm font-medium text-brand-text-secondary transition hover:border-brand-text-secondary/50 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </button>
        </div>
      </div>

      <CsvExportProgress state={exportState} />

      {(FLOW === 'seccap' || FLOW === 'secshot') && <EdgeOnePrefetchAdmin />}

      {diagnosticsOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4">
          <div className="max-h-[85vh] w-full max-w-4xl overflow-auto rounded-lg border border-brand-border bg-brand-surface p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-brand-border pb-4">
              <div>
                <h2 className="flex items-center gap-2 text-base font-semibold text-brand-text-primary">
                  <Activity className="h-4 w-4 text-brand-orange" />
                  Queue Diagnostics
                </h2>
                <p className="mt-1 text-xs text-brand-text-secondary">
                  {FLOW} 当前领取队列，只读。
                  {diagnostics?.model_operator
                    ? `生产票口径：${diagnostics.model_operator} 机器人票 + ${diagnostics.expected_human_votes} 个人工票；进入审计前仍需人工确认。`
                    : `要求 ${diagnostics?.required_production_votes ?? '—'} 张人工生产票。`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDiagnosticsOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary transition hover:text-white"
                title="关闭"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {diagnosticsLoading && (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-brand-text-secondary">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading queue diagnostics...
              </div>
            )}

            {!diagnosticsLoading && diagnosticsError && (
              <div className="py-10 text-center text-sm text-red-400">{diagnosticsError}</div>
            )}

            {!diagnosticsLoading && diagnostics && !diagnosticsError && (
              <div className="space-y-6 pt-5">
                <div className="grid gap-px overflow-hidden rounded-md border border-brand-border bg-brand-border sm:grid-cols-3">
                  {[
                    {
                      title: 'Pending',
                      rows: [
                        ['Total', diagnostics.pending.total],
                        ['Needs first vote', diagnostics.pending.claimable_now],
                        ['Leased / delayed', diagnostics.pending.leased_or_delayed],
                        ['Released', diagnostics.pending.released],
                      ],
                    },
                    {
                      title: 'Voting',
                      rows: [
                        ['Total', diagnostics.voting.total],
                        ['Needs human vote', diagnostics.voting.claimable_now],
                        ['Has model vote', diagnostics.voting.with_model_vote],
                        ['Model + human vote ready', diagnostics.voting.full_production_votes],
                        ['Leased / delayed', diagnostics.voting.leased_or_delayed],
                        ['Released', diagnostics.voting.released],
                        ['Human amend window open', diagnostics.voting.waiting_human_confirmation],
                        ['Ready for audit', diagnostics.voting.ready_to_finalize],
                        ['Excess production votes', diagnostics.voting.excess_production_votes],
                      ],
                    },
                    {
                      title: 'Completed',
                      rows: [
                        ['Total', diagnostics.completed.total],
                        ['Ready for audit', diagnostics.completed.eligible_for_audit],
                        ['Has model vote', diagnostics.completed.with_model_vote],
                        [
                          'Missing required vote',
                          diagnostics.completed.incomplete_production_votes,
                        ],
                        ['Excess production votes', diagnostics.completed.excess_production_votes],
                        [
                          'Legacy count mismatch',
                          diagnostics.completed.legacy_operator_count_mismatch,
                        ],
                      ],
                    },
                  ].map((section) => (
                    <div key={section.title} className="bg-brand-surface p-4">
                      <h3 className="mb-3 text-xs font-semibold uppercase text-brand-text-primary">
                        {section.title}
                      </h3>
                      <dl className="space-y-2">
                        {section.rows.map(([label, value]) => (
                          <div
                            key={label}
                            className="flex items-center justify-between gap-4 text-xs"
                          >
                            <dt className="text-brand-text-secondary">{label}</dt>
                            <dd className="font-mono font-semibold text-brand-text-primary">
                              {Number(value ?? 0).toLocaleString()}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-xs font-semibold uppercase text-brand-text-primary">
                      Voting samples
                    </h3>
                    <span className="text-xs text-brand-text-secondary">
                      Updated {new Date(diagnostics.computed_at_ms).toLocaleString()}
                    </span>
                  </div>
                  <div className="overflow-x-auto rounded-md border border-brand-border">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-brand-dark text-brand-text-secondary">
                        <tr>
                          <th className="px-3 py-2 font-medium">Episode ID</th>
                          <th className="px-3 py-2 text-right font-medium">Human</th>
                          <th className="px-3 py-2 text-right font-medium">All</th>
                          <th className="px-3 py-2 text-right font-medium">Open amend</th>
                          <th className="px-3 py-2 text-right font-medium">Claimable</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-brand-border">
                        {diagnostics.samples.map((row) => (
                          <tr key={row.episode_id} className="text-brand-text-primary">
                            <td className="px-3 py-2 font-mono">{row.episode_id}</td>
                            <td className="px-3 py-2 text-right font-mono">
                              {row.human_vote_count}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{row.all_vote_count}</td>
                            <td className="px-3 py-2 text-right font-mono">
                              {row.open_amend_votes}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span
                                className={clsx(
                                  'font-semibold',
                                  row.claimable_now === 1
                                    ? 'text-emerald-400'
                                    : 'text-brand-text-secondary'
                                )}
                              >
                                {row.claimable_now === 1 ? 'Yes' : 'No'}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {diagnostics.samples.length === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-3 py-8 text-center text-brand-text-secondary"
                            >
                              No voting samples.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => void loadProductionVoteDiagnostics()}
                    className="flex items-center gap-2 rounded-md border border-brand-border px-3 py-2 text-xs text-brand-text-secondary transition hover:text-white"
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiagnosticsOpen(false)}
                    className="rounded-md bg-brand-orange px-3 py-2 text-xs font-semibold text-white transition hover:opacity-90"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            <Users className="h-4 w-4 text-brand-orange" />
            Operators
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {operators.length.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            Total Episodes
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {totalEpisodes.toLocaleString()}
          </div>
          <div className="text-xs text-brand-text-secondary">
            {totalPending.toLocaleString()} pending
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            Total Frames
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {totalFrames.toLocaleString()}
          </div>
          <div className="text-xs text-brand-text-secondary">{avgFrames} avg / episode</div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            Audited Frames
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {totalAuditedFrames.toLocaleString()}
          </div>
          <div className="text-xs text-brand-text-secondary">{auditCoverage}% coverage</div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            Top Auditor
          </div>
          <div className="mt-2 text-lg font-semibold text-brand-text-primary">
            {topAuditor ? topAuditor.name : '—'}
          </div>
          {topAuditor && (
            <div className="text-xs text-brand-text-secondary">
              {(topAuditor.audited_images || 0).toLocaleString()} audited frames
            </div>
          )}
        </div>
      </div>

      {FLOW === 'seccap' && (
        <section
          className="border-y border-brand-border py-5"
          data-testid="model-vote-duration-dashboard"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-brand-surface p-2 text-brand-orange">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-brand-text-primary">
                  ALM Bot Video Duration
                </h2>
                <p className="mt-1 text-xs text-brand-text-secondary">
                  按模型票完成时间统计去重 Episode，最多选择 {leaderboardMaxDaysForFlow()} 天。
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="model-duration-picker">
              <DateRangePicker
                value={modelDurationRange}
                onApply={(value) => {
                  setModelDurationRange(value);
                }}
                onClear={() => {
                  setModelDurationRange({
                    fromDate: '',
                    fromTime: '',
                    toDate: '',
                    toTime: '',
                  });
                  setModelDurationError('');
                }}
              />
              <button
                type="button"
                onClick={() => void loadModelDuration(modelDurationRange)}
                disabled={modelDurationLoading}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-border bg-brand-dark text-brand-text-secondary transition hover:text-white disabled:opacity-50"
                title="Refresh duration"
                aria-label="Refresh ALM Bot duration"
              >
                <RefreshCw className={clsx('h-4 w-4', modelDurationLoading && 'animate-spin')} />
              </button>
            </div>
          </div>

          {modelDurationError && (
            <div className="mt-4 text-xs text-red-300" role="alert">
              {modelDurationError}
            </div>
          )}

          <div className="mt-5 grid gap-4 sm:grid-cols-3 sm:divide-x sm:divide-brand-border">
            <div>
              <div className="text-xs font-semibold uppercase text-brand-text-secondary">
                Total Video Duration
              </div>
              <div
                className="mt-2 font-mono text-3xl font-bold text-brand-text-primary"
                data-testid="model-duration-hours"
              >
                {modelDuration ? `${formatDurationHours(modelDuration.duration_s_total)} h` : '—'}
              </div>
              <div className="mt-1 text-xs text-brand-text-secondary">
                {modelDuration
                  ? formatDurationBreakdown(modelDuration.duration_s_total)
                  : modelDurationLoading
                    ? 'Loading'
                    : 'No data'}
              </div>
            </div>
            <div className="sm:pl-4">
              <div className="text-xs font-semibold uppercase text-brand-text-secondary">
                Episodes
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-brand-text-primary">
                {modelDuration ? modelDuration.episode_count.toLocaleString() : '—'}
              </div>
              <div className="mt-1 text-xs text-brand-text-secondary">Distinct model votes</div>
            </div>
            <div className="sm:pl-4">
              <div className="text-xs font-semibold uppercase text-brand-text-secondary">
                Missing Duration
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-brand-text-primary">
                {modelDuration ? modelDuration.missing_duration_count.toLocaleString() : '—'}
              </div>
              <div className="mt-1 text-xs text-brand-text-secondary">
                {modelDuration
                  ? `${new Date(modelDuration.from_ms).toLocaleString()} - ${new Date(
                      modelDuration.to_ms
                    ).toLocaleString()}`
                  : 'Selected range'}
              </div>
            </div>
          </div>
        </section>
      )}

      {FLOW === 'seccap' && (
        <section className="border-y border-brand-border py-5" data-testid="upstream-ingestion">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-brand-surface p-2 text-brand-orange">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-brand-text-primary">Upstream Ingestion</h2>
              <p className="mt-1 text-xs text-brand-text-secondary">
                Latest successful scheduled Lakeview pull. Existing IDs include every MLP status and
                do not necessarily indicate human annotation.
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-brand-border">
            <div>
              <div className="text-xs font-semibold uppercase text-brand-text-secondary">
                本次拉取新增数据
              </div>
              <div
                className="mt-2 font-mono text-2xl font-bold text-brand-text-primary"
                data-testid="upstream-ingestion-upserted"
              >
                {latestScheduledIngestion
                  ? latestScheduledIngestion.upserted.toLocaleString()
                  : '—'}
              </div>
            </div>
            <div className="lg:pl-4">
              <div className="text-xs font-semibold uppercase text-brand-text-secondary">
                Existing IDs Skipped
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-brand-text-primary">
                {latestScheduledIngestion
                  ? latestScheduledIngestion.already_existing.toLocaleString()
                  : '—'}
              </div>
            </div>
            <div className="lg:pl-4">
              <div className="text-xs font-semibold uppercase text-brand-text-secondary">
                Lakeview Rows
              </div>
              <div className="mt-2 font-mono text-2xl font-bold text-brand-text-primary">
                {latestScheduledIngestion
                  ? latestScheduledIngestion.csv_rows.toLocaleString()
                  : '—'}
              </div>
            </div>
            <div className="lg:pl-4">
              <div className="text-xs font-semibold uppercase text-brand-text-secondary">
                Last Auto Pull
              </div>
              <div className="mt-2 text-sm font-semibold text-brand-text-primary">
                {latestScheduledIngestion
                  ? new Date(latestScheduledIngestion.triggered_at_ms).toLocaleString()
                  : 'Waiting for the first scheduled run'}
              </div>
            </div>
          </div>
        </section>
      )}

      <section
        className="rounded-xl border border-brand-border bg-brand-surface p-5"
        data-testid="daily-workload"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-brand-text-primary">Daily Workload</h2>
            <p className="mt-1 text-xs text-brand-text-secondary">
              {HOURLY_WORKLOAD_SUPPORTED
                ? '按状态事件发生时间逐小时统计；待审工作量按进入待审队列时间统计。时间为 Asia/Shanghai，结束时间不包含在区间内。'
                : `每日新增作业量，按 episode 到达时间统计，最多选择 ${DAILY_WORKLOAD_MAX_DAYS} 天。`}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {HOURLY_WORKLOAD_SUPPORTED && (
              <div
                className="flex h-9 items-center rounded-lg border border-brand-border bg-brand-dark p-1"
                data-testid="workload-display-unit"
              >
                {(['episodes', 'frames'] as const).map((unit) => (
                  <button
                    key={unit}
                    type="button"
                    aria-pressed={workloadDisplayUnit === unit}
                    onClick={() => setWorkloadDisplayUnit(unit)}
                    className={clsx(
                      'h-7 rounded-md px-3 text-xs font-semibold transition',
                      workloadDisplayUnit === unit
                        ? 'bg-brand-surface-highlight text-white'
                        : 'text-brand-text-secondary hover:text-white'
                    )}
                  >
                    {unit === 'episodes' ? 'Episodes' : 'Frames'}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label htmlFor="daily-from" className="text-xs text-brand-text-secondary">
                {HOURLY_WORKLOAD_SUPPORTED ? '开始时间' : '开始日期'}
              </label>
              <input
                id="daily-from"
                type={HOURLY_WORKLOAD_SUPPORTED ? 'datetime-local' : 'date'}
                step={HOURLY_WORKLOAD_SUPPORTED ? 3600 : undefined}
                value={dailyFrom}
                onChange={(e) => {
                  setDailyFrom(e.target.value);
                  setDailyError('');
                }}
                className="h-9 rounded-lg border border-brand-border bg-brand-dark px-3 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="daily-to" className="text-xs text-brand-text-secondary">
                {HOURLY_WORKLOAD_SUPPORTED ? '结束时间' : '结束日期'}
              </label>
              <input
                id="daily-to"
                type={HOURLY_WORKLOAD_SUPPORTED ? 'datetime-local' : 'date'}
                step={HOURLY_WORKLOAD_SUPPORTED ? 3600 : undefined}
                value={dailyTo}
                onChange={(e) => {
                  setDailyTo(e.target.value);
                  setDailyError('');
                }}
                className="h-9 rounded-lg border border-brand-border bg-brand-dark px-3 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={applyDailyRange}
              disabled={dailyLoading || isCsvExporting}
              className="flex h-9 items-center gap-2 rounded-lg border border-brand-border bg-brand-dark px-3 text-xs font-semibold text-brand-text-secondary transition hover:text-white disabled:opacity-50"
            >
              <RefreshCw className={clsx('h-3.5 w-3.5', dailyLoading && 'animate-spin')} />
              Apply range
            </button>
            <button
              type="button"
              onClick={() => void exportDailyWorkload()}
              disabled={dailyRows.length === 0 || isCsvExporting}
              className="flex h-9 items-center gap-2 rounded-lg border border-brand-border bg-brand-dark px-3 text-xs font-semibold text-brand-text-secondary transition hover:text-white disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
          </div>
        </div>
        {dailyError && <p className="mt-3 text-xs text-red-400">{dailyError}</p>}
        <div
          className={clsx(
            'mt-4 grid gap-3',
            HOURLY_WORKLOAD_SUPPORTED ? 'sm:grid-cols-3' : 'sm:grid-cols-4'
          )}
        >
          {!HOURLY_WORKLOAD_SUPPORTED && (
            <div className="rounded-lg border border-brand-border bg-brand-dark p-3">
              <div className="text-xs text-brand-text-secondary">New Episodes</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {dailyTotals.episodes.toLocaleString()}
              </div>
            </div>
          )}
          {!HOURLY_WORKLOAD_SUPPORTED && (
            <div className="rounded-lg border border-brand-border bg-brand-dark p-3">
              <div className="text-xs text-brand-text-secondary">New Frames</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {dailyTotals.frames.toLocaleString()}
              </div>
            </div>
          )}
          {HOURLY_WORKLOAD_SUPPORTED && (
            <div className="rounded-lg border border-brand-border bg-brand-dark p-3">
              <div className="text-xs text-brand-text-secondary">Voting</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {(workloadDisplayUnit === 'frames'
                  ? dailyTotals.votingFrames
                  : dailyTotals.voting
                ).toLocaleString()}
              </div>
              <div
                className="mt-1 font-mono text-xs text-brand-text-secondary"
                data-testid="daily-voting-video-hours-total"
              >
                {formatVideoHours(dailyTotals.votingVideoDurationS)} video hours
              </div>
            </div>
          )}
          <div className="rounded-lg border border-brand-border bg-brand-dark p-3">
            <div className="text-xs text-brand-text-secondary">To Audit</div>
            <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
              {(HOURLY_WORKLOAD_SUPPORTED && workloadDisplayUnit === 'frames'
                ? dailyTotals.toAuditFrames
                : dailyTotals.toAudit
              ).toLocaleString()}
            </div>
            {HOURLY_WORKLOAD_SUPPORTED && (
              <div
                className="mt-1 font-mono text-xs text-brand-text-secondary"
                data-testid="daily-to-audit-video-hours-total"
              >
                {formatVideoHours(dailyTotals.toAuditVideoDurationS)} video hours
              </div>
            )}
          </div>
          <div className="rounded-lg border border-brand-border bg-brand-dark p-3">
            <div className="text-xs text-brand-text-secondary">Audited</div>
            <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
              {(HOURLY_WORKLOAD_SUPPORTED && workloadDisplayUnit === 'frames'
                ? dailyTotals.auditedFrames
                : dailyTotals.audited
              ).toLocaleString()}
            </div>
            {HOURLY_WORKLOAD_SUPPORTED && (
              <div
                className="mt-1 font-mono text-xs text-brand-text-secondary"
                data-testid="daily-audited-video-hours-total"
              >
                {formatVideoHours(dailyTotals.auditedVideoDurationS)} video hours
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 overflow-x-auto rounded-lg border border-brand-border">
          <div
            className={clsx(
              'grid gap-2 border-b border-brand-border bg-brand-surface-highlight/50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary',
              HOURLY_WORKLOAD_SUPPORTED
                ? 'min-w-[720px] grid-cols-[2fr_repeat(4,minmax(0,1fr))]'
                : 'grid-cols-8'
            )}
            data-testid="daily-workload-header"
          >
            <div className="col-span-1">{HOURLY_WORKLOAD_SUPPORTED ? 'Time Interval' : 'Date'}</div>
            {!HOURLY_WORKLOAD_SUPPORTED && (
              <>
                <div className="col-span-1 text-right">Episodes</div>
                <div className="col-span-1 text-right">Frames</div>
                <div className="col-span-1 text-right">Pending</div>
              </>
            )}
            <div className="col-span-1 text-right">Voting</div>
            <div className="col-span-1 text-right">To Audit</div>
            <div className="col-span-1 text-right">Audited</div>
            <div className="col-span-1 text-right">Flow</div>
          </div>
          {dailyLoading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-8 text-xs text-brand-text-secondary">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Loading daily workload...
            </div>
          ) : dailyRows.length > 0 ? (
            <div className="divide-y divide-brand-border">
              {dailyRows.map((row) => (
                <div
                  key={`${row.flow}-${row.date}`}
                  className={clsx(
                    'grid gap-2 px-3 py-2 text-xs text-brand-text-secondary',
                    HOURLY_WORKLOAD_SUPPORTED
                      ? 'min-w-[720px] grid-cols-[2fr_repeat(4,minmax(0,1fr))]'
                      : 'grid-cols-8'
                  )}
                >
                  <div className="col-span-1 font-medium text-brand-text-primary">
                    {HOURLY_WORKLOAD_SUPPORTED
                      ? formatShanghaiHourRange(row.hour_start_ms, row.hour_end_ms)
                      : row.date}
                  </div>
                  {!HOURLY_WORKLOAD_SUPPORTED && (
                    <>
                      <div className="col-span-1 text-right font-mono">
                        {row.new_episodes.toLocaleString()}
                      </div>
                      <div className="col-span-1 text-right font-mono">
                        {row.new_frames.toLocaleString()}
                      </div>
                      <div className="col-span-1 text-right font-mono">
                        {row.pending.toLocaleString()}
                      </div>
                    </>
                  )}
                  <div className="col-span-1 text-right font-mono">
                    <div>
                      {(workloadDisplayUnit === 'frames'
                        ? (row.voting_frames ?? 0)
                        : row.voting
                      ).toLocaleString()}
                    </div>
                    {HOURLY_WORKLOAD_SUPPORTED && (
                      <div className="mt-0.5 text-[10px] text-brand-text-secondary">
                        {formatVideoHours(row.voting_video_duration_s)} h
                      </div>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono">
                    <div>
                      {(HOURLY_WORKLOAD_SUPPORTED && workloadDisplayUnit === 'frames'
                        ? (row.to_audit_frames ?? 0)
                        : row.to_audit
                      ).toLocaleString()}
                    </div>
                    {HOURLY_WORKLOAD_SUPPORTED && (
                      <div className="mt-0.5 text-[10px] text-brand-text-secondary">
                        {formatVideoHours(row.to_audit_video_duration_s)} h
                      </div>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono">
                    <div>
                      {(HOURLY_WORKLOAD_SUPPORTED && workloadDisplayUnit === 'frames'
                        ? (row.audited_frames ?? 0)
                        : row.audited
                      ).toLocaleString()}
                    </div>
                    {HOURLY_WORKLOAD_SUPPORTED && (
                      <div className="mt-0.5 text-[10px] text-brand-text-secondary">
                        {formatVideoHours(row.audited_video_duration_s)} h
                      </div>
                    )}
                  </div>
                  <div className="col-span-1 text-right">{row.flow}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-3 py-8 text-center text-xs text-brand-text-secondary">
              No daily workload in this range.
            </div>
          )}
          <PaginationControls
            pagination={workloadPagination}
            pageSizes={
              HOURLY_WORKLOAD_SUPPORTED ? HOURLY_WORKLOAD_PAGE_SIZES : DAILY_WORKLOAD_PAGE_SIZES
            }
            onPageChange={setWorkloadPage}
            onPageSizeChange={(pageSize) => {
              setWorkloadPage(1);
              setWorkloadPageSize(pageSize);
            }}
            testId="daily-workload-pagination"
          />
        </div>

        <div className="mt-7 border-t border-brand-border pt-5" data-testid="daily-audit-workload">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-brand-text-primary">
                Daily To-Audit Workload
              </h3>
              <p className="mt-1 text-xs text-brand-text-secondary">
                当前待审队列的实际差异帧，占同一批 Episode 完整帧的比例。
              </p>
            </div>
            <button
              type="button"
              onClick={() => void exportDailyAuditWorkload()}
              disabled={dailyAuditRows.length === 0 || isCsvExporting}
              className="flex h-9 items-center gap-2 rounded-lg border border-brand-border bg-brand-dark px-3 text-xs font-semibold text-brand-text-secondary transition hover:text-white disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              Export Audit CSV
            </button>
          </div>

          <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            Selected Range Total
          </div>
          <div className="mt-2 grid gap-3 sm:grid-cols-4">
            <div className="border-l-2 border-brand-orange pl-3">
              <div className="text-xs text-brand-text-secondary">To Audit Episodes</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {dailyAuditTotals.episodes.toLocaleString()}
              </div>
            </div>
            <div className="border-l-2 border-brand-border pl-3">
              <div className="text-xs text-brand-text-secondary">Full Volume</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {dailyAuditTotals.fullHours.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}{' '}
                h
              </div>
            </div>
            <div className="border-l-2 border-brand-border pl-3">
              <div className="text-xs text-brand-text-secondary">Actual Audit Workload</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {dailyAuditTotals.auditHours.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}{' '}
                h
              </div>
            </div>
            <div className="border-l-2 border-brand-border pl-3">
              <div className="text-xs text-brand-text-secondary">Coverage</div>
              <div
                className="mt-1 font-mono text-lg font-semibold text-brand-text-primary"
                data-testid="daily-audit-coverage"
              >
                {dailyAuditCoverage.toFixed(2)}%
              </div>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto border-y border-brand-border">
            <div className="grid min-w-[760px] grid-cols-6 gap-3 border-b border-brand-border bg-brand-surface-highlight/50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
              <div>Date</div>
              <div className="text-right">Episodes</div>
              <div className="text-right">Full Volume</div>
              <div className="text-right">Actual Audit</div>
              <div className="text-right">Coverage</div>
              <div className="text-right">Flow</div>
            </div>
            {dailyLoading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-8 text-xs text-brand-text-secondary">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Loading to-audit workload...
              </div>
            ) : dailyAuditRows.length > 0 ? (
              <div className="divide-y divide-brand-border">
                {dailyAuditRows.map((row) => (
                  <div
                    key={`audit-${row.flow}-${row.date}`}
                    className="grid min-w-[760px] grid-cols-6 gap-3 px-3 py-2 text-xs text-brand-text-secondary"
                  >
                    <div className="font-medium text-brand-text-primary">{row.date}</div>
                    <div className="text-right font-mono">
                      {row.to_audit_episodes.toLocaleString()}
                    </div>
                    <div className="text-right font-mono">{row.full_hours.toFixed(2)} h</div>
                    <div className="text-right font-mono">
                      {row.actual_audit_hours.toFixed(2)} h
                    </div>
                    <div className="text-right font-mono">{row.coverage_pct.toFixed(2)}%</div>
                    <div className="text-right">{row.flow}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3 py-8 text-center text-xs text-brand-text-secondary">
                No current to-audit workload in this range.
              </div>
            )}
            <PaginationControls
              pagination={auditPagination}
              pageSizes={AUDIT_WORKLOAD_PAGE_SIZES}
              onPageChange={setAuditPage}
              onPageSizeChange={(pageSize) => {
                setAuditPage(1);
                setAuditPageSize(pageSize);
              }}
              testId="daily-audit-workload-pagination"
            />
          </div>
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-border bg-brand-surface p-4 text-xs">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand-text-secondary" />
          <input
            type="text"
            value={leaderboardQuery}
            onChange={(e) => setLeaderboardQuery(e.target.value)}
            placeholder="Search operator..."
            className="h-9 w-48 rounded-lg border border-brand-border bg-brand-dark pl-8 pr-3 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-brand-text-secondary">Sort by</span>
          <div className="flex items-center rounded-lg border border-brand-border bg-brand-dark p-0.5">
            <button
              type="button"
              onClick={() => setLeaderboardSortMode('frames')}
              className={clsx(
                'rounded-md px-2 py-1 transition',
                leaderboardSortMode === 'frames'
                  ? 'bg-brand-surface text-white'
                  : 'text-brand-text-secondary hover:text-white'
              )}
            >
              Frames
            </button>
            <button
              type="button"
              onClick={() => setLeaderboardSortMode('episodes')}
              className={clsx(
                'rounded-md px-2 py-1 transition',
                leaderboardSortMode === 'episodes'
                  ? 'bg-brand-surface text-white'
                  : 'text-brand-text-secondary hover:text-white'
              )}
            >
              Episodes
            </button>
            <button
              type="button"
              onClick={() => setLeaderboardSortMode('audited')}
              className={clsx(
                'rounded-md px-2 py-1 transition',
                leaderboardSortMode === 'audited'
                  ? 'bg-brand-surface text-white'
                  : 'text-brand-text-secondary hover:text-white'
              )}
            >
              Audited
            </button>
          </div>
        </div>
        {leaderboardQuery && (
          <button
            type="button"
            onClick={() => setLeaderboardQuery('')}
            className="rounded-md border border-brand-border px-2.5 py-1 font-semibold text-brand-text-secondary hover:text-white"
          >
            Clear Search
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (isCsvExporting) {
              showToast('正在导出，请稍候');
              return;
            }
            setExportMenuOpen(true);
          }}
          disabled={!hasAnyData || isCsvExporting}
          className="ml-auto flex items-center gap-2 rounded-lg border border-brand-border bg-brand-dark px-3 py-2 font-semibold text-brand-text-secondary transition hover:text-white disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
        <div className="text-brand-text-secondary">
          Showing {sortedOperators.length.toLocaleString()} of {operators.length.toLocaleString()}
        </div>
      </div>

      <div className="rounded-xl border border-brand-border bg-brand-surface overflow-hidden shadow-sm">
        {hasData ? (
          <div className="w-full">
            <div className="grid grid-cols-12 gap-2 border-b border-brand-border bg-brand-surface-highlight/50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
              <div className="col-span-1">Rank</div>
              <div className="col-span-3">Operator</div>
              <div className="col-span-1 text-right">Episodes</div>
              <div className="col-span-2 text-right">Frames</div>
              <div className="col-span-1 text-right">Audited Ep</div>
              <div className="col-span-1 text-right">Audited Fr</div>
              <div className="col-span-1 text-right">Accuracy</div>
              <div className="col-span-2 text-right">Task Quality</div>
            </div>

            <div className="divide-y divide-brand-border">
              {sortedOperators.map((op, index) => {
                const rank = index + 1;
                let rankIcon = null;
                let rankColor = 'text-brand-text-secondary';

                if (rank === 1) {
                  rankIcon = <Medal className="h-5 w-5 text-yellow-500" />;
                  rankColor = 'text-yellow-500';
                } else if (rank === 2) {
                  rankIcon = <Medal className="h-5 w-5 text-gray-300" />;
                  rankColor = 'text-gray-300';
                } else if (rank === 3) {
                  rankIcon = <Medal className="h-5 w-5 text-amber-700" />;
                  rankColor = 'text-amber-700';
                }

                const framePercent = Math.round((op.images / maxFrames) * 100);

                return (
                  <div
                    key={op.name}
                    className="grid grid-cols-12 items-center gap-2 px-4 py-4 transition-colors hover:bg-brand-surface-highlight"
                  >
                    <div
                      className={clsx(
                        'col-span-1 font-mono font-bold flex items-center gap-1',
                        rankColor
                      )}
                    >
                      <span className="w-6">{rankIcon || `#${rank}`}</span>
                    </div>
                    <div className="col-span-3 font-medium text-brand-text-primary flex items-center gap-2 truncate">
                      <div className="h-6 w-6 rounded-full bg-brand-dark border border-brand-border flex items-center justify-center text-[10px] text-brand-text-secondary flex-shrink-0">
                        {op.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="truncate" title={op.name}>
                        {op.name}
                      </span>
                    </div>
                    <div className="col-span-1 text-right font-mono text-brand-text-secondary">
                      {op.episodes.toLocaleString()}
                    </div>
                    <div className="col-span-2 flex flex-col items-end gap-1">
                      <span className="font-mono font-bold text-brand-text-primary">
                        {op.images.toLocaleString()}
                      </span>
                      <div className="h-1.5 w-full bg-brand-dark rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-orange"
                          style={{ width: `${framePercent}%` }}
                        />
                      </div>
                    </div>
                    <div className="col-span-1 text-right font-mono text-brand-text-secondary">
                      {(op.audited_episodes || 0).toLocaleString()}
                    </div>
                    <div className="col-span-1 text-right font-mono text-brand-text-secondary">
                      {(op.audited_images || 0).toLocaleString()}
                    </div>
                    <div className="col-span-1 text-right font-mono text-brand-text-secondary">
                      {(() => {
                        const acc = operatorAccuracy(op);
                        return acc == null ? '—' : `${(acc * 100).toFixed(2)}%`;
                      })()}
                    </div>
                    <div className="col-span-2 text-right font-mono text-brand-text-secondary">
                      {(() => {
                        const tqAcc = operatorTaskQualityAccuracy(op);
                        return tqAcc == null ? '—' : `${(tqAcc * 100).toFixed(2)}%`;
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : hasAnyData ? (
          <div className="p-12 text-center text-brand-text-secondary">
            No operators match the current search.
          </div>
        ) : (
          <div className="p-12 text-center text-brand-text-secondary">
            No operator data available yet.
          </div>
        )}
      </div>

      {exportMenuOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-[360px] rounded-xl border border-brand-border bg-brand-surface p-6 shadow-xl space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-brand-text-primary">Export CSV</h2>
              <p className="mt-1 text-xs text-brand-text-secondary">
                Select the data source to export.
              </p>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => openExportPicker('annotate')}
                disabled={isCsvExporting}
                className="flex w-full items-center justify-between rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-left text-sm text-brand-text-primary transition hover:border-brand-text-secondary/50 disabled:opacity-50"
              >
                <span>Annotator Leaderboard</span>
                <Download className="h-4 w-4 text-brand-text-secondary" />
              </button>
              <button
                type="button"
                onClick={() => openExportPicker('audit')}
                disabled={isCsvExporting}
                className="flex w-full items-center justify-between rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-left text-sm text-brand-text-primary transition hover:border-brand-text-secondary/50 disabled:opacity-50"
              >
                <span>Auditor Leaderboard</span>
                <Download className="h-4 w-4 text-brand-text-secondary" />
              </button>
              <button
                type="button"
                onClick={() => openExportPicker('accuracy')}
                disabled={isCsvExporting}
                className="flex w-full items-center justify-between rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-left text-sm text-brand-text-primary transition hover:border-brand-text-secondary/50 disabled:opacity-50"
              >
                <span>Accuracy + Task Quality</span>
                <Download className="h-4 w-4 text-brand-text-secondary" />
              </button>
              {ZERO_SCORE_SUPPORTED && (
                <button
                  type="button"
                  onClick={openZeroScoreExport}
                  disabled={isCsvExporting}
                  className="flex w-full items-center justify-between rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-left text-sm text-brand-text-primary transition hover:border-brand-text-secondary/50 disabled:opacity-50"
                >
                  <span>Zero-score Work</span>
                  <Download className="h-4 w-4 text-brand-text-secondary" />
                </button>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setExportMenuOpen(false)}
                className="rounded-lg border border-brand-border px-3 py-1.5 text-xs text-brand-text-secondary transition hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-80 rounded-xl border border-brand-border bg-brand-surface p-6 shadow-xl space-y-4">
            <h2 className="text-sm font-semibold text-brand-text-primary">
              {exportPickerConfig(pickerMode).title}
            </h2>
            {exportPickerConfig(pickerMode).hardMaxDays != null && (
              <p className="text-xs font-medium text-brand-orange">
                日期范围上限：{exportPickerConfig(pickerMode).hardMaxDays} 天
              </p>
            )}
            <p className="text-xs text-brand-text-secondary">
              {exportPickerConfig(pickerMode).rangeHint}
            </p>
            <div className="space-y-3">
              <div className="flex flex-col gap-1">
                <label htmlFor="range-from" className="text-xs text-brand-text-secondary">
                  开始日期
                </label>
                <input
                  id="range-from"
                  type="date"
                  value={rangeFrom}
                  onChange={(e) => {
                    setRangeFrom(e.target.value);
                    setRangeError('');
                  }}
                  className="rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="range-to" className="text-xs text-brand-text-secondary">
                  结束日期
                </label>
                <input
                  id="range-to"
                  type="date"
                  value={rangeTo}
                  onChange={(e) => {
                    setRangeTo(e.target.value);
                    setRangeError('');
                  }}
                  className="rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
                />
              </div>
            </div>
            {rangeError && <p className="text-xs text-red-400">{rangeError}</p>}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setPickerMode(null)}
                disabled={isCsvExporting}
                className="rounded-lg border border-brand-border px-3 py-1.5 text-xs text-brand-text-secondary hover:text-white transition disabled:opacity-50"
              >
                取消
              </button>
              {exportPickerConfig(pickerMode).allowAllTime && (
                <button
                  type="button"
                  onClick={() => void handleDownloadAllTime()}
                  disabled={isCsvExporting}
                  className="rounded-lg border border-brand-border px-3 py-1.5 text-xs text-brand-text-secondary hover:text-white transition disabled:opacity-50"
                >
                  导出全部时间
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleDownloadRange()}
                disabled={isCsvExporting}
                className="rounded-lg bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition disabled:opacity-50"
              >
                导出所选区间
              </button>
            </div>
          </div>
        </div>
      )}

      {zeroScoreOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-[520px] rounded-xl border border-brand-border bg-brand-surface p-6 shadow-xl space-y-5">
            <div>
              <h2 className="text-sm font-semibold text-brand-text-primary">
                导出 0 分标注量级 CSV
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-brand-text-secondary">
                统计 task_quality_type 为 staged_unacceptable 的 annotator / auditor 工作量。
              </p>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                Date range
              </div>
              <DateRangePicker
                value={zeroScoreRange}
                onApply={(value) => {
                  setZeroScoreRange(value);
                  setZeroScoreError('');
                }}
                onClear={() => {
                  setZeroScoreRange({ fromDate: '', fromTime: '', toDate: '', toTime: '' });
                  setZeroScoreError('');
                }}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                  Role
                </div>
                <div className="inline-flex overflow-hidden rounded-lg border border-brand-border bg-brand-dark p-1">
                  {(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'annotate', label: 'Annotate' },
                      { value: 'audit', label: 'Audit' },
                    ] satisfies Array<{ value: ZeroScoreRole; label: string }>
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setZeroScoreRole(option.value)}
                      className={clsx(
                        'rounded-md px-3 py-1.5 text-sm font-medium transition',
                        zeroScoreRole === option.value
                          ? 'bg-brand-orange text-white shadow-sm'
                          : 'text-brand-text-secondary hover:text-white'
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
                  View
                </div>
                <div className="inline-flex overflow-hidden rounded-lg border border-brand-border bg-brand-dark p-1">
                  {(
                    [
                      { value: 'summary', label: 'Summary' },
                      { value: 'detail', label: 'Detail' },
                    ] satisfies Array<{ value: ZeroScoreView; label: string }>
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setZeroScoreView(option.value)}
                      className={clsx(
                        'rounded-md px-3 py-1.5 text-sm font-medium transition',
                        zeroScoreView === option.value
                          ? 'bg-brand-surface-highlight text-brand-text-primary shadow-sm'
                          : 'text-brand-text-secondary hover:text-white'
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {zeroScoreError && <p className="text-xs text-red-400">{zeroScoreError}</p>}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setZeroScoreOpen(false)}
                disabled={isCsvExporting}
                className="rounded-lg border border-brand-border px-3 py-1.5 text-xs text-brand-text-secondary hover:text-white transition disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void executeZeroScoreExport()}
                disabled={isCsvExporting}
                className="flex items-center gap-2 rounded-lg bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {slowExportConfirm != null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="w-96 rounded-xl border border-red-500/40 bg-brand-surface p-6 shadow-xl space-y-4">
            <h2 className="text-sm font-semibold text-red-400">⚠️ 大范围导出确认</h2>
            <p className="text-xs leading-relaxed text-brand-text-secondary">
              你选择了{' '}
              <span className="font-semibold text-white">{slowExportConfirm.diffDays} 天</span>的
              {exportPickerConfig(slowExportConfirm.mode).confirmKindLabel}数据（超过{' '}
              {exportPickerConfig(slowExportConfirm.mode).slowWarnDays} 天）。
            </p>
            {exportPickerConfig(slowExportConfirm.mode).hardMaxDays != null && (
              <p className="text-xs leading-relaxed text-brand-text-secondary">
                本平台单次导出上限为{' '}
                <span className="font-semibold text-white">
                  {exportPickerConfig(slowExportConfirm.mode).hardMaxDays} 天
                </span>
                （当前选择仍在允许范围内）。
              </p>
            )}
            <p className="text-xs leading-relaxed text-brand-text-secondary">
              此操作会在后台扫描大量 episode
              {slowExportConfirm.mode === 'accuracy' ? ' 并逐条比对' : ''}，
              <span className="text-red-300">可能非常慢</span>
              ，并占用 Durable Object 资源，存在<span className="text-red-300">请求超时</span>风险。
            </p>
            <p className="text-xs leading-relaxed text-brand-text-secondary">
              强烈建议在<span className="font-semibold text-white">业务低峰时段</span>
              操作。确定要继续吗？
            </p>
            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={() => setSlowExportConfirm(null)}
                disabled={isCsvExporting}
                className="rounded-lg border border-brand-border px-3 py-1.5 text-xs text-brand-text-secondary hover:text-white transition disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void executeDownloadRange(slowExportConfirm.mode)}
                disabled={isCsvExporting}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 transition disabled:opacity-50"
              >
                我了解风险，继续导出
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
